import { useGetCmsTheme } from "@/api/cms";
import CmsSidebarNav from "@/components/cms/CmsSidebarNav";
import { CmsPageAppearanceSaveRegistrar } from "@/components/cms/theme/CmsPageAppearanceSaveRegistrar";
import ConfirmNavigation from "@/components/navigation/ConfirmNavigation";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import {
  CmsPageAppearanceProvider,
  useCmsPageAppearance,
} from "@/context/cms/CmsPageAppearanceContext";
import { CmsThemeEditorProvider } from "@/context/cms/CmsThemeEditorContext";
import type { CmsColor } from "@/lib/cms/cms-theme.constants";
import { loadCmsGoogleFonts } from "@/lib/cms/cms-google-fonts";
import { cn } from "@/lib/utils";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

interface CMSLayoutProps {
  children: React.ReactNode;
  breadcrumbs?: {
    label: string;
    to?: string;
  }[];
  hideSubmitButton?: boolean;
  hidePageAppearanceButton?: boolean;
  pageName: string;
  pageSubtitle?: string;
  submitable?: boolean;
  formIds: string[];
  havePublishedSwitch?: boolean;
  isPublished?: boolean;
  setIsPublished?: (isPublished: boolean) => void;
  isValid?: boolean;
  isLoading?: boolean;
  className?: string;
}

const GLOBAL_APPEARANCE_PATH = "/cms/apparence";

function CmsPageAppearanceHeaderButton() {
  const { t } = useTranslation();
  const { formId, setOpen } = useCmsPageAppearance();

  return (
    <Button
      type="button"
      variant="outline"
      className="text-sm font-semibold font-['Raleway'] leading-normal"
      disabled={!formId}
      onClick={() => setOpen(true)}
    >
      {t("cms.page.appearance.open")}
    </Button>
  );
}

const CMSLayout = ({
  children,
  pageName,
  pageSubtitle,
  formIds,
  havePublishedSwitch = false,
  isPublished,
  setIsPublished,
  isLoading = false,
  className,
  isValid,
  breadcrumbs,
  hideSubmitButton,
  hidePageAppearanceButton,
}: CMSLayoutProps) => {
  const location = useLocation();

  const { instance } = useAuth();

  const { t } = useTranslation();
  const { data: cmsTheme } = useGetCmsTheme();
  const [editorColors, setEditorColors] = useState<CmsColor[]>([]);

  useEffect(() => {
    if (!cmsTheme?.colors) return;
    // Évite setState si le contenu est identique (refetch thème / nouvelle
    // référence tableau) — sinon CmsThemeEditorProvider re-render toute la page.
    setEditorColors((prev) => {
      const next = cmsTheme.colors;
      if (
        prev.length === next.length &&
        prev.every(
          (c, i) =>
            c.id === next[i]?.id &&
            c.hex === next[i]?.hex &&
            c.name === next[i]?.name,
        )
      ) {
        return prev;
      }
      return next;
    });
  }, [cmsTheme?.colors]);

  useEffect(() => {
    void loadCmsGoogleFonts();
  }, []);

  const ids = useMemo(() => {
    return formIds && formIds.length > 0 ? formIds : [];
  }, [formIds]);

  const dirtyFormsRef = React.useRef<Set<string>>(new Set());
  const [isDirty, setIsDirty] = React.useState<boolean>(false);

  React.useEffect(() => {
    const cleanups: Array<() => void> = [];

    const markDirty = (id: string) => {
      if (!dirtyFormsRef.current.has(id)) {
        dirtyFormsRef.current.add(id);
        setIsDirty(true);
      }
    };

    const markClean = (id: string) => {
      dirtyFormsRef.current.delete(id);
      setIsDirty(dirtyFormsRef.current.size > 0);
    };

    ids.forEach((id) => {
      const formElement = document.getElementById(id);
      if (!(formElement instanceof HTMLFormElement)) return;

      formElement.addEventListener("input", () => markDirty(id));
      formElement.addEventListener("reset", () => markClean(id));
      formElement.addEventListener("submit", () => markClean(id));

      cleanups.push(() => {
        formElement.removeEventListener("input", () => markDirty(id));
        formElement.removeEventListener("reset", () => markClean(id));
        formElement.removeEventListener("submit", () => markClean(id));
      });
    });

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [ids]);

  const handleSubmitAllForms = React.useCallback(() => {
    ids.forEach((id) => {
      const formElement = document.getElementById(id);
      if (!(formElement instanceof HTMLFormElement)) return;
      if (typeof formElement.requestSubmit === "function") {
        formElement.requestSubmit();
      } else {
        formElement.submit();
      }
    });
  }, [ids]);

  const showPageAppearanceButton =
    hidePageAppearanceButton !== true &&
    location.pathname !== GLOBAL_APPEARANCE_PATH;

  return (
    <CmsPageAppearanceProvider>
      <CmsPageAppearanceSaveRegistrar
        submitAll={handleSubmitAllForms}
        isSaveDisabled={!(isValid ?? true)}
      />
      <div className="bg-[#F7FAFC]">
        <div className="max-w-screen-2xl m-auto pt-8  px-8 ">
          <div className="border-b border-slate-200 pb-2 mb-8">
            <p className="text-[#0c111d] text-[32px] font-semibold font-['Instrument Sans'] leading-loose text-start">
              {t("cmsEnteteCatalogueRaisonne")} {instance ?? "Unknow instance"}
            </p>
          </div>
          <div className="justify-start items-start gap-16 inline-flex w-full">
            <CmsSidebarNav />
            <div className="w-full flex-col justify-start items-start gap-8 inline-flex">
              {breadcrumbs && (
                <BreadcrumbList>
                  {breadcrumbs.flatMap((breadcrumb, index) => [
                    breadcrumb.to ? (
                      <BreadcrumbItem key={breadcrumb.label}>
                        <BreadcrumbLink asChild>
                          <Link to={breadcrumb.to}>{breadcrumb.label}</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                    ) : (
                      <BreadcrumbItem key={breadcrumb.label}>
                        <BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>
                      </BreadcrumbItem>
                    ),
                    ...(index < breadcrumbs.length - 1
                      ? [<BreadcrumbSeparator key={`sep-${index}`} />]
                      : []),
                  ])}
                </BreadcrumbList>
              )}
              <div className="w-full pb-5 border-b border-slate-200 justify-between items-center inline-flex">
                <div className="justify-start flex flex-col items-start">
                  <p className="text-[#0c111d] text-lg font-semibold font-['Raleway'] leading-normal">
                    {pageName}
                  </p>
                  {pageSubtitle && (
                    <p className="max-w-prose text-sm text-slate-500 font-['Raleway'] leading-relaxed">
                      {pageSubtitle}
                    </p>
                  )}
                </div>
                <div className="justify-start items-center gap-4 flex">
                  {havePublishedSwitch && (
                    <div className="items-center flex gap-1">
                      <Switch
                        checked={isPublished}
                        onCheckedChange={() =>
                          setIsPublished ? setIsPublished(!isPublished) : null
                        }
                      />
                      <Label className="text-slate-700 text-sm font-medium font-['Raleway'] leading-normal">
                        {t("cmsPublierPageLabel")}
                      </Label>
                    </div>
                  )}
                  {showPageAppearanceButton && (
                    <CmsPageAppearanceHeaderButton />
                  )}
                  {hideSubmitButton !== true && (
                    <Button
                      type="button"
                      onClick={handleSubmitAllForms}
                      variant="default"
                      className="text-white text-sm font-semibold font-['Raleway'] leading-normal"
                      disabled={!(isValid ?? true)}
                    >
                      {t("cmsEnregistrerBouton")}
                    </Button>
                  )}
                </div>
              </div>
              <div
                className={cn(
                  "justify-start items-start gap-8 inline-flex w-full pb-10",
                  className,
                )}
              >
                {isLoading ? (
                  <Skeleton className="w-full h-full" />
                ) : (
                  <CmsThemeEditorProvider
                    colors={editorColors}
                    onColorsChange={setEditorColors}
                  >
                    {children}
                  </CmsThemeEditorProvider>
                )}
              </div>
            </div>
          </div>
        </div>
        <ConfirmNavigation
          isBlocked={isDirty}
          title={t("cms.confirmNavigation.title")}
        />
      </div>
    </CmsPageAppearanceProvider>
  );
};

export default CMSLayout;
